import { FormItem } from "./FormItem";
import { asKey } from "./asKey";
import {
  Autocomplete,
  Checkbox,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  FormHelperText,
  InputLabel,
  Tooltip,
} from "@mui/material";
import styles from "../styles/Reports.module.scss";
import React, { useEffect, useState } from "react";
// import { useFormAction } from "react-router-dom";

interface IBasicInputProps {
  name: string;
  type?: string;
  children?: any;
  label?: string;
  id?: string;
  defaultValue?: any;
  className?: any;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export function BasicInput(props: IBasicInputProps) {
  const {
    name,
    children,
    label,
    id,
    defaultValue,
    disabled,
    required,
    error,
    helperText,
  } = props;
  const labelText = label ?? name[0].toUpperCase() + name.slice(1);
  return (
    <FormItem>
      <TextField
        fullWidth
        id={id ?? name}
        size="small"
        variant="outlined"
        label={labelText}
        name={name}
        defaultValue={defaultValue}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
      />
      {/*<input id={id ?? name} type={type} className={styles.basicInput} name={name} defaultValue={defaultValue} defaultChecked={type == 'checkbox' ? defaultValue : null }/>*/}
      {children}
    </FormItem>
  );
}

export interface ControlledTextboxProps {
  name: string;
  label?: string;
  value: string | null;
  updateValue: (name: string, value: string) => void;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  type?: string;
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
}

export function ControlledTextbox(props: ControlledTextboxProps) {
  const {
    name,
    label,
    value,
    updateValue,
    disabled,
    required,
    helperText,
    type,
    placeholder,
    multiline,
    minRows,
    maxRows,
  } = props;

  return (
    <FormItem>
      <TextField
        variant={"outlined"}
        size={"small"}
        fullWidth
        name={name}
        label={label ?? name}
        value={value ?? ""}
        onChange={(e) => updateValue(name, e.target.value)}
        disabled={disabled}
        required={required}
        error={
          !!helperText || (required &&
          (value === null || (typeof value === "string" && value.trim() === "")))
        }
        helperText={helperText}
        type={type}
        placeholder={placeholder}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
      />
    </FormItem>
  );
}

interface IBasicDropDownProps {
  name: string;
  options: string[] | any[];
  disableCustom?: boolean;
  label?: string;
  defaultValue?: any;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  helperText?: string;
}

export function BasicDropDown(props: IBasicDropDownProps) {
  const {
    name,
    options,
    disableCustom,
    label,
    defaultValue,
    hidden,
    disabled,
    required,
    helperText,
  } = props;
  const labelName = label ?? name[0].toUpperCase() + name.slice(1);

  const filter = createFilterOptions();
  return (
    <FormItem>
      <FormControl fullWidth>
        <Autocomplete
          id={asKey(name)}
          hidden={hidden}
          size="small"
          disabled={disabled}
          defaultValue={defaultValue}
          freeSolo={true}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          filterOptions={(options, params) =>
            doFilter(options, params, filter, disableCustom)
          }
          getOptionLabel={getOptionLabel}
          options={options}
          renderInput={(params) => (
            <TextField
              name={asKey(name)}
              {...params}
              required={required}
              label={labelName}
              size={"small"}
              style={{ lineHeight: "1rem" }}
              ref={params.InputProps.ref}
              error={!!helperText}
              helperText={helperText}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>{option.label ?? option}</li>
          )}
          fullWidth={true}
        />
      </FormControl>
    </FormItem>
  );
}

interface ControlledDropDownProps extends IBasicDropDownProps {
  value: string | null;
  updateValue: (name: string, val: any) => void;
  sx?: SxProps<Theme>;
}

export function ControlledDropDown(props: ControlledDropDownProps) {
  const {
    name,
    options,
    disableCustom,
    label,
    disabled,
    required,
    helperText,
    value,
    updateValue,
    sx,
  } = props;
  const labelName = label ?? name[0].toUpperCase() + name.slice(1);

  const filter = createFilterOptions();
  return (
    <FormItem>
      <FormControl fullWidth>
        <Autocomplete
          id={asKey(name)}
          value={value}
          onChange={(e, val) => updateValue(name, val)}
          size="small"
          disabled={disabled}
          freeSolo={true}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          filterOptions={(options, params) =>
            doFilter(options, params, filter, disableCustom)
          }
          getOptionLabel={getOptionLabel}
          options={options}
          renderInput={(params) => (
            <TextField
              name={asKey(name)}
              {...params}
              required={required}
              label={labelName}
              size={"small"}
              style={{ lineHeight: "1rem" }}
              ref={params.InputProps.ref}
              error={!!helperText}
              helperText={helperText}
              sx={sx}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>{option.label ?? option}</li>
          )}
          fullWidth={true}
        />
      </FormControl>
    </FormItem>
  );
}

function doFilter(options, params, filter, disableCustom) {
  const newOptions = filter(options, params);
  const { inputValue } = params;
  const isExisting = options.some((option) => inputValue === option);
  if (inputValue !== "" && !isExisting && !disableCustom) {
    newOptions.push({ inputValue, label: `Add ${inputValue}` });
  }
  return newOptions;
}

function getOptionLabel(option) {
  if (typeof option === "string") {
    return option;
  }
  if (option.inputValue) {
    return option.inputValue;
  }
  return option.label;
}

export function BasicCheckbox(props: {
  label?;
  name;
  defaultValue;
  className?;
}) {
  const { label, name, defaultValue, className } = props;
  const labelText = label ?? name[0].toUpperCase() + name.slice(1);

  return (
    <FormItem className={className}>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              sx={{ padding: 0 }}
              name={name}
              defaultChecked={defaultValue}
            />
          }
          label={labelText}
        />
      </FormGroup>
    </FormItem>
  );
}

export function ControlledCheckbox(props: {
  label?;
  name;
  value;
  setValue;
  className?;
  indeterminate?;
  disabled?;
  sx?;
}) {
  const { label, name, value, setValue, className, indeterminate, disabled, sx } = props;
  const labelText = label ?? name[0].toUpperCase() + name.slice(1);

  return (
    <FormItem className={className}>
      <FormGroup row>
        <FormControlLabel
          disabled={disabled}
          control={
            <Checkbox
              sx={{ padding: 0, ...sx }}
              name={name}
              checked={value}
              indeterminate={indeterminate}
              disabled={disabled}
              onChange={(e) => setValue(e.target.checked)}
            />
          }
          label={labelText}
        />
      </FormGroup>
    </FormItem>
  );
}

export function ControlledSelect(props) {
  return (
    <FormItem>
      <FormControl fullWidth>
        <Select size={"small"} value={props.value} onChange={props.onChange}>
          {props.options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FormItem>
  );
}

export function LoadingSpinner({ width }) {
  return (
    <svg
      role="status"
      className={styles.spinner}
      width={width}
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
  );
}
